import { isStringEqualIgnoreCase } from '../common-utils.js';
import { z } from 'zod';
import { ClassEligibility } from './coach-availabilities.js';
import { lessonSchema } from './lesson.js';

export const learningPathLessonSchema = z.object({
  order: z.number().int(),
  lessonId: z.string(),
  lesson: lessonSchema,
});
export type LearningPathLesson = z.infer<typeof learningPathLessonSchema>;

export const learningPathSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export type LearningPath = z.infer<typeof learningPathSchema>;

export const learningPathWithLessonsSchema = learningPathSchema.extend({
  learningPathLessons: z.array(learningPathLessonSchema),
});
export type LearningPathWithLessons = z.infer<typeof learningPathWithLessonsSchema>;

export const createLearningPathOptionsSchema = z.object({
  name: z.string(),
  lessons: z.array(
    z.object({
      lessonId: z.string(),
      order: z.number().int(),
    }),
  ),
});
export type CreateLearningPathOptions = z.infer<typeof createLearningPathOptionsSchema>;

export const updateLearningPathOptionsSchema = z.object({
  name: z.string(),
  lessons: z.array(
    z.object({
      lessonId: z.string(),
      order: z.number().int(),
    }),
  ),
});

export type UpdateLearningPathOptions = z.infer<typeof updateLearningPathOptionsSchema>;

/** Empty CSV cells parse as ""; missing columns become undefined — coerce both to "". */
const csvString = z
  .string()
  .optional()
  .nullable()
  .transform((val) => val ?? '');

export const createLearningPathCsvRow = z.object({
  learningPathName: z.string().min(1, 'Learning path name is required'),
  lessonName: z.string().min(1, 'Lesson name is required'),
  nextLessonName: csvString,
});
export type CreateLearningPathCsvRow = z.infer<typeof createLearningPathCsvRow>;

export const CreateLearningPathCsvFormKeys = {
  csvFile: 'csvFile',
} as const;

export const GetLearningPathResultsSchema = z.object({
  learningPaths: z.array(learningPathWithLessonsSchema),
});
export type GetLearningPathResults = z.infer<typeof GetLearningPathResultsSchema>;

export const learningPathNamesSchema = z.enum([
  'Regular',
  'Regular - extended',
  'Beginner',
  'Beginner - extended',
  'Middle School',
  'Intermediate',
  'Fluency',
  'Fluency (3)',
  'Fluency (1)',
  'Fluency (2)',
  'Practice (Zoom)',
  'Practice (Google Meet)',
  'Diagnostic',
  'Diagnostic w/ Practice (Zoom)',
  'Diagnostic w/ Practice (Google Meet)',
  'Makeup Diagnostic',
  'Read Aloud',
  'Assigned GLA',
  'Regular (a)',
  'Regular (b)',
  'Regular (c)',
  'Regular (d)',
  'Fluency 1(a)',
  'Fluency 1(b)',
  'Fluency 1(c)',
  'Fluency 1(d)',
  'Fluency 2(a)',
  'Fluency 2(b)',
  'Fluency 2(c)',
  'Fluency 2(d)',
  'Fluency 3(b)',
  'Fluency 3(c)',
  'Fluency 3(d)',
]);
export const LearningPathNames = learningPathNamesSchema.enum;
export type LearningPathNames = z.infer<typeof learningPathNamesSchema>;
export const learningPathNamesArray = learningPathNamesSchema.options;

export const interventionLearningPathNames: string[] = [
  LearningPathNames.Regular,
  LearningPathNames['Regular - extended'],
  LearningPathNames['Regular (a)'],
  LearningPathNames['Regular (b)'],
  LearningPathNames['Regular (c)'],
  LearningPathNames['Regular (d)'],
  LearningPathNames.Beginner,
  LearningPathNames['Beginner - extended'],
  LearningPathNames['Middle School'],
  LearningPathNames.Intermediate,
  LearningPathNames['Fluency (3)'],
  LearningPathNames['Fluency (1)'],
  LearningPathNames['Fluency (2)'],
  LearningPathNames['Fluency 1(a)'],
  LearningPathNames['Fluency 1(b)'],
  LearningPathNames['Fluency 1(c)'],
  LearningPathNames['Fluency 1(d)'],
  LearningPathNames['Fluency 2(a)'],
  LearningPathNames['Fluency 2(b)'],
  LearningPathNames['Fluency 2(c)'],
  LearningPathNames['Fluency 2(d)'],
  LearningPathNames['Fluency 3(b)'],
  LearningPathNames['Fluency 3(c)'],
  LearningPathNames['Fluency 3(d)'],
  LearningPathNames['Read Aloud'],
];

/**
 * Learning paths whose student groups should appear in the Admin Teacher Attendance
 * view. Mirrors the "regular" student groups that show up in the teacher portal's
 * My Student Groups section (see AIR-1813 / AIR-1868). Practice, diagnostic,
 * fluency, read aloud, and assigned GLA classes are intentionally excluded.
 */
export const attendanceTrackedLearningPathNames: string[] = [
  LearningPathNames.Regular,
  LearningPathNames['Regular - extended'],
  LearningPathNames['Regular (a)'],
  LearningPathNames['Regular (b)'],
  LearningPathNames['Regular (c)'],
  LearningPathNames['Regular (d)'],
  LearningPathNames.Beginner,
  LearningPathNames['Beginner - extended'],
  LearningPathNames['Middle School'],
  LearningPathNames.Intermediate,
];

export const practiceLearningPathNames: string[] = [
  LearningPathNames['Practice (Google Meet)'],
  LearningPathNames['Practice (Zoom)'],
];

export const diagnosticLearningPathNames: string[] = [
  LearningPathNames.Diagnostic,
  LearningPathNames['Diagnostic w/ Practice (Google Meet)'],
  LearningPathNames['Diagnostic w/ Practice (Zoom)'],
  LearningPathNames['Makeup Diagnostic'],
];

/** When the most recent student group uses one of these paths, end lesson is the last non-cancelled lesson before them. */
export const endLessonFluencyLookbackLearningPathNames: string[] = [
  LearningPathNames.Fluency,
  LearningPathNames['Fluency (1)'],
  LearningPathNames['Fluency (2)'],
  LearningPathNames['Fluency (3)'],
  LearningPathNames['Fluency 1(a)'],
  LearningPathNames['Fluency 1(b)'],
  LearningPathNames['Fluency 1(c)'],
  LearningPathNames['Fluency 1(d)'],
  LearningPathNames['Fluency 2(a)'],
  LearningPathNames['Fluency 2(b)'],
  LearningPathNames['Fluency 2(c)'],
  LearningPathNames['Fluency 2(d)'],
  LearningPathNames['Fluency 3(b)'],
  LearningPathNames['Fluency 3(c)'],
  LearningPathNames['Fluency 3(d)'],
  LearningPathNames['Read Aloud'],
];

export const returningStudentFluencyRemapSourcePathNames: string[] = [
  LearningPathNames.Regular,
  LearningPathNames['Regular - extended'],
  LearningPathNames['Regular (a)'],
  LearningPathNames['Regular (b)'],
  LearningPathNames['Regular (c)'],
  LearningPathNames['Regular (d)'],
  LearningPathNames.Beginner,
  LearningPathNames['Beginner - extended'],
  LearningPathNames.Intermediate,
  LearningPathNames['Middle School'],
];

/** Lettered fluency paths, whose lessons use the `F1(c) - 1` spelling. */
export const letteredFluencyLearningPathNames: string[] = [
  LearningPathNames['Fluency 1(a)'],
  LearningPathNames['Fluency 1(b)'],
  LearningPathNames['Fluency 1(c)'],
  LearningPathNames['Fluency 1(d)'],
  LearningPathNames['Fluency 2(a)'],
  LearningPathNames['Fluency 2(b)'],
  LearningPathNames['Fluency 2(c)'],
  LearningPathNames['Fluency 2(d)'],
  LearningPathNames['Fluency 3(b)'],
  LearningPathNames['Fluency 3(c)'],
  LearningPathNames['Fluency 3(d)'],
];

/**
 * Learning paths a bulk CSV update can look a starting lesson up in, and assign (AIR-2691):
 * the returning-student remap sources plus the lettered fluency paths.
 */
export const bulkUpdateStartingLessonLearningPathNames: string[] = [
  ...returningStudentFluencyRemapSourcePathNames,
  ...letteredFluencyLearningPathNames,
];

type LearningPathLessonRef = { lessonId: string };
type LearningPathWithLessonRefs = { name: string; learningPathLessons: LearningPathLessonRef[] };

/** Curriculum lesson names may end with a session-type tag, e.g. `1.b-3 (GLA)`, `1.b-6 (PM)`. */
const LESSON_NAME_TAG_PATTERN = /\s*\((?:GLA|PM)\)\s*$/i;

/** Level-lesson shape, with an optional `F` fluency prefix and an optional dot before the sub-level. */
const LESSON_NAME_PATTERN = /^(F?)(\d+)\.?([a-zA-Z]?)-(\d+)$/;

/**
 * Parenthesized sub-level shape used by the lettered curriculum paths, e.g. `1(b) - 1` on
 * `Regular (b)`. These are distinct lessons from the dotted `1.b-1` on `Beginner`, so they
 * keep the parentheses in their key.
 */
const PARENTHESIZED_LESSON_NAME_PATTERN = /^(F?)(\d+)\s*\(\s*([a-zA-Z])\s*\)\s*-\s*(\d+)$/;

/**
 * Canonical lookup key for a curriculum lesson name. The session-type tag, the dot before the
 * sub-level and the spacing are dropped, so `1.b-3 (GLA)`, `1.b-3` and `1b-3` all resolve to the
 * same lesson, while `1(b) - 3` and `1(b)-3` resolve to the separate `1(b)-3` lesson. Names
 * outside both shapes (e.g. `Diagnostic`) key on themselves.
 */
export function normalizeLessonNameKey(name: string): string {
  const untagged = name.trim().replace(LESSON_NAME_TAG_PATTERN, '').trim();

  const parenthesized = PARENTHESIZED_LESSON_NAME_PATTERN.exec(untagged);
  if (parenthesized) {
    const [, fluency, level, letter, lessonNum] = parenthesized;
    return `${fluency.toLowerCase()}${level}(${letter.toLowerCase()})-${lessonNum}`;
  }

  const match = LESSON_NAME_PATTERN.exec(untagged);
  if (!match) {
    return untagged.toLowerCase();
  }

  const [, fluency, level, letter, lessonNum] = match;
  return `${fluency.toLowerCase()}${level}${letter.toLowerCase()}-${lessonNum}`;
}

/** `Level Intervention` prefix of the level-lesson source format; optional, so `4(a), 18` parses too. */
const LEVEL_INTERVENTION_PATTERN = /^(?:Level\s+Intervention\s+)?(\d+)(?:\s*\(([a-zA-Z])\))?\s*,\s*(\d+)\s*$/i;

/**
 * Normalize curriculum starting-lesson source values into {@link normalizeLessonNameKey} keys.
 * Examples:
 * - Level Intervention 2, 19 → 2-19
 * - Level Intervention 4(a), 18 → 4a-18
 * - 4(a), 18 → 4a-18
 * - Level Intervention 1(b), 7 → 1b-7
 * Also accepts lesson names as they appear in the curriculum (`1.b-32`, `1.b-3 (GLA)`, `2-19`),
 * including the lettered-path spelling (`1(b) - 1`), which stays distinct from `1.b-1`.
 */
export function normalizeStartingLessonSource(source: string): string | null {
  const trimmed = source.trim();
  if (!trimmed) {
    return null;
  }

  const untagged = trimmed.replace(LESSON_NAME_TAG_PATTERN, '').trim();
  if (PARENTHESIZED_LESSON_NAME_PATTERN.test(untagged) || LESSON_NAME_PATTERN.test(untagged)) {
    return normalizeLessonNameKey(untagged);
  }

  const intervention = LEVEL_INTERVENTION_PATTERN.exec(trimmed);
  if (!intervention) {
    return null;
  }

  const [, level, letter, lessonNum] = intervention;
  return `${level}${letter ? letter.toLowerCase() : ''}-${lessonNum}`;
}

/**
 * Resolve which learning path to assign for a lesson that appears in the remap source paths.
 * The lesson's own learning path is used; the rules below only break ties for lessons that
 * several paths share (AIR-2183 AC 2.c.3.a–c / AIR-2691 AC 5):
 * - in all remap sources → Regular
 * - in both Beginner and Beginner - extended → Beginner
 * - in exactly one remap source → that path
 * - in several remap sources otherwise → Regular
 * - in none → null
 */
export function resolveLearningPathNameForLessonInRemapSources(
  lessonId: string,
  learningPaths: LearningPathWithLessonRefs[],
): string | null {
  const containing = new Set<string>();
  for (const learningPath of learningPaths) {
    if (learningPath.learningPathLessons.some((lpl) => lpl.lessonId === lessonId)) {
      containing.add(learningPath.name);
    }
  }

  const containingRemapPaths = returningStudentFluencyRemapSourcePathNames.filter((pathName) =>
    containing.has(pathName),
  );

  if (containingRemapPaths.length === 0) {
    return null;
  }

  if (containingRemapPaths.length === returningStudentFluencyRemapSourcePathNames.length) {
    return LearningPathNames.Regular;
  }

  if (containing.has(LearningPathNames.Beginner) && containing.has(LearningPathNames['Beginner - extended'])) {
    return LearningPathNames.Beginner;
  }

  // Lessons unique to one path (e.g. Middle School's `3.b-1`) must not fall back to Regular,
  // which does not contain them.
  if (containingRemapPaths.length === 1) {
    return containingRemapPaths[0];
  }

  return LearningPathNames.Regular;
}

/**
 * Resolve which learning path a bulk CSV update should assign for a starting lesson (AIR-2691).
 * Lessons on the remap source paths keep the tie-breaks of
 * {@link resolveLearningPathNameForLessonInRemapSources}; fluency lessons resolve to the lettered
 * fluency path that holds them. Returns null when no supported path contains the lesson.
 */
export function resolveLearningPathNameForStartingLesson(
  lessonId: string,
  learningPaths: LearningPathWithLessonRefs[],
): string | null {
  const containing = new Set(
    learningPaths
      .filter((learningPath) => learningPath.learningPathLessons.some((lpl) => lpl.lessonId === lessonId))
      .map((learningPath) => learningPath.name),
  );

  const supported = bulkUpdateStartingLessonLearningPathNames.filter((pathName) => containing.has(pathName));
  if (supported.length === 0) {
    return null;
  }

  if (supported.length === 1) {
    return supported[0];
  }

  const remapResolved = resolveLearningPathNameForLessonInRemapSources(lessonId, learningPaths);
  if (remapResolved && supported.includes(remapResolved)) {
    return remapResolved;
  }

  // Deterministic fallback: the first supported path in declaration order.
  return supported[0];
}

export const studentGroupTypeSchema = z.enum(['one_time', 'recurring']);
export type StudentGroupType = z.infer<typeof studentGroupTypeSchema>;
export const StudentGroupType = studentGroupTypeSchema.enum;
export const StudentGroupTypeLabelMap: Record<StudentGroupType, string> = {
  [StudentGroupType.one_time]: 'One-time',
  [StudentGroupType.recurring]: 'Recurring',
};

/** Learning paths for student groups shown under the admin Type filter → One-time. */
export const oneTimeStudentGroupLearningPathNames: string[] = [
  LearningPathNames['Practice (Google Meet)'],
  LearningPathNames['Practice (Zoom)'],
  LearningPathNames.Diagnostic,
  LearningPathNames['Diagnostic w/ Practice (Google Meet)'],
  LearningPathNames['Diagnostic w/ Practice (Zoom)'],
];

/** Learning paths for student groups shown under the admin Type filter → Recurring. */
export const recurringStudentGroupLearningPathNames: string[] = [
  LearningPathNames.Regular,
  LearningPathNames['Regular - extended'],
  LearningPathNames['Regular (a)'],
  LearningPathNames['Regular (b)'],
  LearningPathNames['Regular (c)'],
  LearningPathNames['Regular (d)'],
  LearningPathNames.Beginner,
  LearningPathNames['Beginner - extended'],
  LearningPathNames.Fluency,
  LearningPathNames['Fluency (1)'],
  LearningPathNames['Fluency (2)'],
  LearningPathNames['Fluency (3)'],
  LearningPathNames['Fluency 1(a)'],
  LearningPathNames['Fluency 1(b)'],
  LearningPathNames['Fluency 1(c)'],
  LearningPathNames['Fluency 1(d)'],
  LearningPathNames['Fluency 2(a)'],
  LearningPathNames['Fluency 2(b)'],
  LearningPathNames['Fluency 2(c)'],
  LearningPathNames['Fluency 2(d)'],
  LearningPathNames['Fluency 3(b)'],
  LearningPathNames['Fluency 3(c)'],
  LearningPathNames['Fluency 3(d)'],
  LearningPathNames.Intermediate,
  LearningPathNames['Makeup Diagnostic'],
  LearningPathNames['Middle School'],
  LearningPathNames['Read Aloud'],
];

export function learningPathNamesForStudentGroupTypes(types: StudentGroupType[]): string[] {
  const names = new Set<string>();

  for (const type of types) {
    if (type === StudentGroupType.one_time) {
      for (const name of oneTimeStudentGroupLearningPathNames) {
        names.add(name);
      }
    } else if (type === StudentGroupType.recurring) {
      for (const name of recurringStudentGroupLearningPathNames) {
        names.add(name);
      }
    }
  }

  return [...names];
}

/**
 * Class eligibility tags required for a coach to teach sessions under this learning path.
 * Used when filtering "Available teachers" (admin class edit, bulk update, sub teachers).
 */
export function classEligibilitiesRequiredForLearningPathName(name: string): ClassEligibility[] {
  if (interventionLearningPathNames.some((learningPathName) => isStringEqualIgnoreCase(learningPathName, name))) {
    return [ClassEligibility.can_teach_intervention];
  }

  if (practiceLearningPathNames.some((learningPathName) => isStringEqualIgnoreCase(learningPathName, name))) {
    return [ClassEligibility.can_teach_practice];
  }

  if (diagnosticLearningPathNames.some((learningPathName) => isStringEqualIgnoreCase(learningPathName, name))) {
    return [ClassEligibility.can_teach_diagnostic];
  }

  return [ClassEligibility.can_teach_intervention];
}

/**
 * Union of class eligibility tags required across multiple learning paths (e.g. bulk teacher update).
 * When several paths are selected, a coach must have every returned tag to appear as available
 * (server filters with `classEligibilities: { hasEvery: [...] }`).
 */
export function classEligibilitiesRequiredForLearningPathNames(names: string[]): ClassEligibility[] {
  const uniqueNames = [...new Set(names)];
  if (uniqueNames.length === 0) {
    return classEligibilitiesRequiredForLearningPathName('');
  }
  return [...new Set(uniqueNames.flatMap(classEligibilitiesRequiredForLearningPathName))];
}
